import React, { useState, useEffect, useRef } from "react";
import { useAudio } from "../providers/AudioProvider";
import { TRANSCRIPTION_CLEAR_TIMER_MS } from "../utils/constants";
import PrivacyPolicyModal from "./modals/PrivacyPolicyModal";

interface ConversationModeModalProps {
  isOpen: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  isModelLoaded?: boolean;
  modelLoadingProgress?: string;
  tutorialDismissed?: boolean;
}

interface AcknowledgedTriggers {
  commands: boolean;
  silence: boolean;
  voiceNotice: boolean;
  dontShowAgain: boolean;
}

const ConversationModeModal: React.FC<ConversationModeModalProps> = ({
  isOpen,
  onConfirm,
  onCancel,
  isModelLoaded = true,
  modelLoadingProgress = "",
  tutorialDismissed = false,
}) => {
  const { initializeVoice } = useAudio();
  const initializeVoiceCalledRef = useRef(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [acknowledgedTriggers, setAcknowledgedTriggers] =
    useState<AcknowledgedTriggers>({
      commands: false,
      silence: false,
      voiceNotice: false,
      dontShowAgain: false,
    });
  const [minViewTimerActive, setMinViewTimerActive] = useState(false);
  const [showFullTutorial, setShowFullTutorial] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);

  const totalSteps = 5;

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setCurrentStep(0);
      setAcknowledgedTriggers({
        commands: false,
        silence: false,
        voiceNotice: false,
        dontShowAgain: false,
      });
      setMinViewTimerActive(true);
      setShowFullTutorial(!tutorialDismissed);

      // Start minimum view timer (1.5 seconds)
      const timer = setTimeout(() => {
        setMinViewTimerActive(false);
      }, 1500);

      return () => clearTimeout(timer);
    }
  }, [isOpen, tutorialDismissed]);

  // Start loading the speech model when modal opens and model isn't loaded
  useEffect(() => {
    if (isOpen && !isModelLoaded && !initializeVoiceCalledRef.current) {
      initializeVoiceCalledRef.current = true;
      initializeVoice().catch((error) => {
        console.error("Failed to start loading speech model:", error);
      });
    }
  }, [isOpen, isModelLoaded, initializeVoice]);

  // Handle step change with timer reset
  const handleStepChange = (newStep: number) => {
    setCurrentStep(newStep);
    setMinViewTimerActive(true);

    const timer = setTimeout(() => {
      setMinViewTimerActive(false);
    }, 1500);

    return () => clearTimeout(timer);
  };

  const handleNext = () => {
    if (currentStep < totalSteps - 1) {
      handleStepChange(currentStep + 1);
    } else {
      // Final step - enable conversation mode
      if (acknowledgedTriggers.dontShowAgain) {
        localStorage.setItem("conversationModeTutorialDismissed", "true");
      }
      onConfirm();
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      handleStepChange(currentStep - 1);
    }
  };

  const handleCancel = () => {
    onCancel();
  };

  const handleTriggerAcknowledge = (trigger: keyof AcknowledgedTriggers) => {
    setAcknowledgedTriggers((prev) => ({
      ...prev,
      [trigger]: !prev[trigger],
    }));
  };

  const canProceed = () => {
    if (minViewTimerActive) return false;

    // Step 2: requires send, clear, and exit checkboxes
    if (currentStep === 1) {
      return acknowledgedTriggers.commands;
    }

    // Step 3: requires silence checkbox
    if (currentStep === 2) {
      return acknowledgedTriggers.silence;
    }

    // Step 5: no checkboxes required
    if (currentStep === 3) {
      return true;
    }

    // Step 6: requires voiceNotice checkbox AND model to be loaded
    if (currentStep === 4) {
      return acknowledgedTriggers.voiceNotice && isModelLoaded;
    }

    // Steps 0 and 1: only require timer
    return true;
  };

  if (!isOpen) return null;

  const AccentCard: React.FC<{ title?: string; children: React.ReactNode }> = ({
    title,
    children,
  }) => (
    <div className="relative rounded-lg border border-gray-200 bg-gray-50 p-4">
      <div
        className="absolute inset-y-0 left-0 w-1 rounded-l-lg bg-[#46ACC6]"
        aria-hidden
      />
      {title ? (
        <h3 className="text-sm font-medium text-gray-900 mb-1">{title}</h3>
      ) : null}
      <div className="text-sm text-gray-700">{children}</div>
    </div>
  );

  const renderSimplifiedView = () => {
    return (
      <div className="space-y-4">
        <h2 className="text-xl font-semibold text-gray-900">Hands-Free Mode</h2>

        <p className="text-sm text-gray-700">
          If you want a full refresher on hands-free mode,
          <button
            onClick={() => setShowFullTutorial(true)}
            className="ml-1 text-[#46ACC6] underline decoration-1 underline-offset-2 hover:text-[#3a8ba3] focus:outline-none focus:ring-2 focus:ring-[#46ACC6] focus:ring-offset-2 rounded"
          >
            open the instructions
          </button>
          .
        </p>

        {/* Consolidated guidance card */}
        <AccentCard title="Before you enable">
          <ul className="list-disc pl-5 space-y-1.5">
            <li>
              Hands-Free continuously listens and transcribes what you say and
              can mis-trigger on phrases like "send message".
            </li>
            <li>
              <span className="font-medium">No sensitive info.</span> Don’t
              recite PHI or other confidential details — this service is{" "}
              <span className="font-semibold">not HIPAA-compliant</span>.
            </li>
          </ul>
        </AccentCard>

        {/* Slim inline loader */}
        {!isModelLoaded && (
          <div
            role="status"
            className="flex items-center gap-2 rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-800"
          >
            <span className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-blue-600 border-t-transparent border-r-transparent border-l-transparent" />
            <span>
              {modelLoadingProgress ||
                "One second while we get speech recognition ready…"}
            </span>
          </div>
        )}
      </div>
    );
  };

  const renderSlide = () => {
    switch (currentStep) {
      case 0:
        return (
          <div className="space-y-4">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              What is Hands-Free Mode?
            </h2>

            <div className="space-y-3 text-gray-700 text-sm">
              <p>
                Hands-free mode lets you talk to the Assistant using only your
                voice—no typing needed.
              </p>
              <p>
                The Assistant transcribes what you say live, and reads replies
                aloud.
              </p>
            </div>
          </div>
        );

      case 1:
        return (
          <div className="space-y-4">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              Send messages with your voice
            </h2>

            <div className="space-y-3 text-gray-700 text-sm">
              <p>
                As you talk, your words show up live. You can speak the
                following commands to control hands-free mode:
              </p>
              <ul className="list-disc pl-5 space-y-1">
                <li>
                  <strong>Send</strong> — say <em>“send message”</em>
                </li>
                <li>
                  <strong>Clear</strong> — say <em>“clear message”</em>
                </li>
                <li>
                  <strong>Interrupt</strong> — say <em>“stop talking”</em>{" "}
                  (stops spoken response)
                </li>
                <li>
                  <strong>Exit</strong> — say <em>“exit hands-free mode”</em>{" "}
                  (turns off listening)
                </li>
              </ul>
            </div>

            <div className="space-y-2">
              <label className="flex items-center space-x-2 text-sm text-gray-500">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-gray-300 text-[#46ACC6] focus:ring-[#46ACC6]"
                  checked={acknowledgedTriggers.commands}
                  onChange={() => handleTriggerAcknowledge("commands")}
                />
                <span>I understand the voice commands.</span>
              </label>
            </div>
          </div>
        );
      case 2:
        return (
          <div className="space-y-4">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              Silence = Auto-Clear
            </h2>

            <div className="space-y-3 text-gray-700 text-sm">
              <p>
                If you're{" "}
                <strong>
                  silent for {TRANSCRIPTION_CLEAR_TIMER_MS / 1000} seconds
                </strong>
                , the message in the box will{" "}
                <strong>automatically clear</strong>.
              </p>
            </div>

            <div className="space-y-2">
              <label className="flex items-center space-x-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-gray-300 text-[#46ACC6] focus:ring-[#46ACC6]"
                  checked={acknowledgedTriggers.silence}
                  onChange={() => handleTriggerAcknowledge("silence")}
                />
                <span>
                  I understand a {TRANSCRIPTION_CLEAR_TIMER_MS / 1000}-second
                  silence clears the message.
                </span>
              </label>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-4">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              Control Videos with Your Voice
            </h2>

            <div className="space-y-3 text-gray-700 text-sm">
              <p>
                If a reply contains a video, say <strong>"play video"</strong>{" "}
                to open it fullscreen and start playback.
              </p>
              <p>
                While a video is open, you can say{" "}
                <strong>"pause video"</strong>, <strong>"play video"</strong>,
                or <strong>"close video"</strong>.
              </p>
            </div>
          </div>
        );
      case 4:
        return (
          <div className="space-y-5 max-h-[60vh] overflow-y-auto pr-2 [&::-webkit-scrollbar]:w-3 [&::-webkit-scrollbar-track]:bg-gray-200 [&::-webkit-scrollbar-thumb]:bg-gray-400 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:hover:bg-gray-500 [&::-webkit-scrollbar-thumb]:border-2 [&::-webkit-scrollbar-thumb]:border-white scrollbar-thin scrollbar-track-gray-200 scrollbar-thumb-gray-400 hover:scrollbar-thumb-gray-500">
            <h2 className="text-xl font-semibold text-gray-900">
              Hands-Free Privacy & Safety
            </h2>

            {/* What this mode does */}
            <div className="space-y-3 text-gray-700 text-sm">
              <p>
                While Hands-Free is on, Aveta Assistant continuously listens to
                enable voice control.
              </p>

              {/* Data & privacy summary */}
              <AccentCard title="What happens with your data">
                <ul className="list-disc pl-5 space-y-1">
                  <li>
                    Audio is processed only on your device and never stored or
                    shared.
                  </li>
                  <li>
                    <strong>Messages you send</strong> are stored to deliver
                    replies and may be reviewed for quality.
                  </li>
                  <li>
                    Voice commands may be{" "}
                    <strong>misheard or mis-triggered</strong> (e.g., “send
                    message”), which could send a text you didn't intend to
                    send.
                  </li>
                  <li>
                    See the full privacy policy{" "}
                    <button
                      type="button"
                      className="text-[#46ACC6] hover:underline focus:outline-none"
                      onClick={() => setShowPrivacyModal(true)}
                    >
                      here
                    </button>
                    .
                  </li>{" "}
                </ul>
              </AccentCard>
            </div>

            {/* Quick controls as compact pills */}
            <div className="text-xs">
              <p className="mb-2 font-medium text-gray-700">
                Quick safety controls
              </p>
              <div className="flex flex-wrap gap-2">
                <span className="inline-flex items-center rounded-md bg-gray-100 px-2.5 py-1 font-medium text-gray-700">
                  To stop listening: Say “Exit Hands-Free Mode” or Press "Exit".
                </span>
                <span className="inline-flex items-center rounded-md bg-gray-100 px-2.5 py-1 font-medium text-gray-700">
                  To clear a draft message: Say “Clear Message”.
                </span>
                <span className="inline-flex items-center rounded-md bg-gray-100 px-2.5 py-1 font-medium text-gray-700">
                  To interrupt a reply: Say “Stop Talking”.
                </span>
              </div>
            </div>

            {/* Acknowledgements */}
            <div className="space-y-2">
              <label className="flex items-start space-x-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  className="mt-0.5 h-4 w-4 rounded border-gray-300 text-[#46ACC6] focus:ring-[#46ACC6]"
                  checked={acknowledgedTriggers.voiceNotice}
                  onChange={() => handleTriggerAcknowledge("voiceNotice")}
                />
                <span>
                  I understand Hands-Free continuously listens/transcribes, can
                  mis-trigger on phrases like "send message", is not
                  HIPAA-compliant, and I will not recite PHI.
                </span>
              </label>

              <label className="flex items-center space-x-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-gray-300 text-[#46ACC6] focus:ring-[#46ACC6]"
                  checked={acknowledgedTriggers.dontShowAgain}
                  onChange={() => handleTriggerAcknowledge("dontShowAgain")}
                />
                <span>Do not show this again on this browser.</span>
              </label>
            </div>

            {/* Model Loading Status */}
            {!isModelLoaded && (
              <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
                <div className="flex items-center space-x-2">
                  <span className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-blue-600 border-t-transparent border-r-transparent border-l-transparent" />
                  <div className="text-sm text-blue-800">
                    {modelLoadingProgress ||
                      "One second while we get speech recognition ready..."}
                  </div>
                </div>
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="py-6 pl-6 pr-4 bg-white rounded-lg shadow-xl max-w-md w-full relative">
        {/* Cancel button - X in top right corner */}
        <button
          onClick={handleCancel}
          className="absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500 rounded-full transition-colors"
          aria-label="Close"
        >
          <svg
            className="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>

        <div className="overflow-y-auto max-h-[90dvh] [&::-webkit-scrollbar]:w-3 [&::-webkit-scrollbar-track]:bg-gray-200 [&::-webkit-scrollbar-thumb]:bg-gray-400 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:hover:bg-gray-500 [&::-webkit-scrollbar-thumb]:border-2 [&::-webkit-scrollbar-thumb]:border-white scrollbar-thin scrollbar-track-gray-200 scrollbar-thumb-gray-400 hover:scrollbar-thumb-gray-500">
          <div className="pr-3">
            {/* Progress indicator - only show for full tutorial */}
            {showFullTutorial && (
              <div className="flex justify-center mb-4">
                <div className="flex space-x-2">
                  {Array.from({ length: totalSteps }, (_, i) => (
                    <div
                      key={i}
                      className={`w-2 h-2 rounded-full ${
                        i <= currentStep ? "bg-[#46ACC6]" : "bg-gray-300"
                      }`}
                    />
                  ))}
                </div>
              </div>
            )}

            {showFullTutorial ? renderSlide() : renderSimplifiedView()}

            {/* Navigation buttons */}
            {showFullTutorial ? (
              <div className="flex space-x-3 mt-6">
                {currentStep > 0 && (
                  <button
                    onClick={handleBack}
                    className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors"
                  >
                    Back
                  </button>
                )}

                <button
                  onClick={handleNext}
                  disabled={!canProceed()}
                  className={`flex-1 px-4 py-2 text-sm font-medium text-white border border-transparent rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 transition-colors ${
                    canProceed()
                      ? "bg-[#46ACC6] hover:bg-[#3a8ba3] focus:ring-[#46ACC6]"
                      : "bg-gray-400 cursor-not-allowed"
                  }`}
                >
                  {currentStep === totalSteps - 1 ? "Enable" : "Next"}
                </button>
              </div>
            ) : (
              <div className="flex space-x-3 mt-6">
                <button
                  onClick={onConfirm}
                  disabled={!isModelLoaded}
                  className={`flex-1 px-4 py-2 text-sm font-medium text-white border border-transparent rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 transition-colors ${
                    isModelLoaded
                      ? "bg-[#46ACC6] hover:bg-[#3a8ba3] focus:ring-[#46ACC6]"
                      : "bg-gray-400 cursor-not-allowed"
                  }`}
                >
                  Enable
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Privacy Policy Modal */}
      <PrivacyPolicyModal
        isOpen={showPrivacyModal}
        onClose={() => setShowPrivacyModal(false)}
      />
    </div>
  );
};

export default ConversationModeModal;
